import Downshift, { type ControllerStateAndHelpers, type GetItemPropsOptions } from 'downshift'
import classes from './DropdownToggle.module.scss'
import {
  type CSSProperties,
  type ReactNode,
  useRef,
  useState
} from 'react'
import { FaCheck, FaTimes } from 'react-icons/fa'
import { ScrollIntoView, ToggleButton } from '../..'

// Define the item type explicitly
interface DropdownItem {
  value: string
}

const itemToString = (option?: DropdownItem | null) => option
  ? option.value
  : ''

export interface toggle {
  label: string
  value: boolean
}

export interface Props {
  preferedPosition?: 'right-auto' | 'right'
  onSelect: (value: toggle) => void
  toggles: toggle[]
  renderButton: (renderButtonProps: any) => ReactNode
}

const DropdownToggle = ({
  toggles,
  onSelect,
  renderButton,
  preferedPosition
}: Props) => {
  const comboBoxRef = useRef<HTMLDivElement>(null)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)

  const renderOptions = (getItemProps: (options: GetItemPropsOptions<DropdownItem>) => any) =>
    toggles.map(toggle => {
      const handleOnClick = () => {
        onSelect({
          label: toggle.label,
          value: !toggle.value
        })
      }

      const itemProps = getItemProps({
        item: { value: toggle.label },
        onClick: handleOnClick
      })

      return (
        <li
          aria-selected
          key={toggle.label}
          role='option'
          {...itemProps}
        >
          <ToggleButton
            iconOff={<FaTimes />}
            iconOn={<FaCheck />}
            isOn={toggle.value}
            isToggleLeft={false}
            label={toggle.label}
            onChange={handleOnClick}
          />
        </li>
      )
    })

  const handleResize = () => {
    if (preferedPosition === 'right-auto') setWindowWidth(window.innerWidth)
  }

  window.addEventListener('resize', handleResize)

  const getComboBoxLeftPositionStyle = (comboBoxRef: React.RefObject<HTMLDivElement>): CSSProperties => {
    if (preferedPosition !== 'right-auto') return { }

    if (!comboBoxRef.current) {
      return { left: '0' }
    }

    const comboxBoxPosition = comboBoxRef.current.getBoundingClientRect().left + 302 // 302 = dropdown width + shadow + gap position below icon

    const MAX_HORIZONTAL_SCROLL_WIDTH = 1400 // body width (1366) + browser frame width with scroll (34)
    const MAX_SIDE_CONTENT_WIDTH = 716 // side content = side navigation bar width + container innter padding left

    const contentWidth = windowWidth < MAX_HORIZONTAL_SCROLL_WIDTH
      ? MAX_HORIZONTAL_SCROLL_WIDTH
      : windowWidth

    // handling when dropdown is render just next to side navigation on the left, partial of the dropdown will be blocked
    if (comboxBoxPosition < contentWidth - MAX_SIDE_CONTENT_WIDTH) {
      return { left: '0' }
    }

    // handling when dropdown is render at the end of the windows on the right, partial of the dropdown will be block or horizontal scroll happend to show the dropdown
    if (comboxBoxPosition > windowWidth) {
      return { left: `${windowWidth - comboxBoxPosition}px` }
    }

    return { }
  }

  const className = classes.DropdownContainer + (
    preferedPosition && preferedPosition.includes('right')
      ? ' ' + classes.RightAligned
      : ''
  )

  return (
    <div
      ref={
        preferedPosition === 'right-auto'
          ? comboBoxRef
          : undefined
      }
    >
      <Downshift
        itemToString={itemToString}
        selectedItem={null}
        stateReducer={(_state, changes) => {
          // When item is selected, prevent the menu from closing
          if (changes.type === Downshift.stateChangeTypes.clickItem) {
            return {
              ...changes,
              isOpen: true // Keep the menu open
            }
          }

          return changes
        }}
      >
        {
          ({
            getItemProps,
            getMenuProps,
            getToggleButtonProps,
            isOpen
          }: ControllerStateAndHelpers<DropdownItem>) =>
            <span className={className}>
              {renderButton(getToggleButtonProps)}
              <span
                className={classes.listWrapper}
                style={getComboBoxLeftPositionStyle(comboBoxRef)}
              >
                <ScrollIntoView
                  isActive={isOpen}
                  withAnimated
                >
                  <ul {...getMenuProps()}>
                    {
                      isOpen &&
                    renderOptions(getItemProps)
                    }
                  </ul>
                </ScrollIntoView>
              </span>
            </span>
        }
      </Downshift>
    </div>
  )
}

export default DropdownToggle
